"use client";

import { useEffect } from "react";
import type { AttractionId } from "@/lib/attractions/types";
import { trackEvent } from "@/lib/analytics/track";

const ENGAGED_MS = 10_000;
const SCROLL_RATIO = 0.5;

export default function QualifiedVisitorTracker({ attractionId }: { attractionId: AttractionId }) {
  useEffect(() => {
    const storageKey = `machuping:qualified-visitor:${attractionId}`;
    let recorded = false;

    try {
      recorded = window.sessionStorage.getItem(storageKey) === "1";
    } catch {
      // Session storage can be unavailable in hardened browser modes.
    }
    if (recorded) return;

    const qualify = (qualificationMethod: "engaged_10s" | "scroll_50") => {
      if (recorded) return;
      recorded = true;
      try {
        window.sessionStorage.setItem(storageKey, "1");
      } catch {
        // Tracking still works when storage is unavailable.
      }
      trackEvent("qualified_visitor", {
        attraction_id: attractionId,
        qualification_method: qualificationMethod,
      });
      window.removeEventListener("scroll", onScroll);
    };

    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable > 0 && window.scrollY / scrollable >= SCROLL_RATIO) qualify("scroll_50");
    };
    const timer = window.setTimeout(() => {
      if (document.visibilityState === "visible") qualify("engaged_10s");
    }, ENGAGED_MS);

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [attractionId]);

  return null;
}
